import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { PortalHeader, ClientTimeline } from "@/components/portal/ClientTimeline";
import { ClientUpdates } from "@/components/portal/ClientUpdates";
import { ClientReports } from "@/components/portal/ClientReports";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [portal, setPortal] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [reports, setReports] = useState([]);
  const [notSetUp, setNotSetUp] = useState(false);

  useEffect(() => {
    const boot = async () => {
      let u = location.state?.user;
      if (!u) {
        try {
          u = (await axios.get(`${API}/auth/me`, { withCredentials: true })).data;
        } catch {
          navigate("/login");
          return;
        }
      }
      setUser(u);
      try {
        const res = await axios.get(`${API}/portal/me`, { withCredentials: true });
        setPortal(res.data);
        const [up, rep] = await Promise.all([
          axios.get(`${API}/portal/me/updates`, { withCredentials: true }),
          axios.get(`${API}/portal/me/reports`, { withCredentials: true }),
        ]);
        setUpdates(up.data.updates);
        setReports(rep.data.reports);
      } catch (e) {
        if (e.response?.status === 404) setNotSetUp(true);
      }
    };
    boot();
  }, [location.state, navigate]);

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    navigate("/");
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#060D1A] flex items-center justify-center" data-testid="portal-loading">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#060D1A] text-slate-100" data-testid="portal-page">
      <PortalHeader user={user} right={
        <>
          {user.role === "admin" && (
            <Link to="/portal/admin" data-testid="portal-admin-link" className="inline-flex items-center rounded-full border border-white/15 px-4 py-2 text-sm text-slate-300 transition-colors duration-300 hover:bg-white hover:text-[#060D1A]">
              Admin Console
            </Link>
          )}
          <Link to="/dashboard" data-testid="portal-billing-link" className="inline-flex items-center rounded-full border border-white/15 px-4 py-2 text-sm text-slate-300 transition-colors duration-300 hover:bg-white hover:text-[#060D1A]">
            Billing & Downloads
          </Link>
          <button onClick={handleLogout} data-testid="portal-logout-button" className="inline-flex items-center rounded-full border border-white/15 px-4 py-2 text-sm text-slate-300 transition-colors duration-300 hover:bg-white hover:text-[#060D1A]">
            Log out
          </button>
        </>
      } />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12 space-y-8">
        {notSetUp ? (
          <div className="rounded-3xl border border-white/15 bg-[#0F1B31]/80 p-10 text-center" data-testid="portal-not-setup">
            <h1 className="font-display text-2xl font-bold text-white">Your portal is being set up</h1>
            <p className="mt-3 text-slate-400 max-w-md mx-auto">
              Owen activates your 30-day progress portal after your kickoff call. Questions? Call 705-433-2050.
            </p>
          </div>
        ) : !portal ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <div>
              <p className="font-mono-label text-xs uppercase tracking-[0.3em] text-slate-500">{portal.client.plan}</p>
              <h1 className="font-display text-3xl font-bold text-white mt-2" data-testid="portal-client-name">{portal.client.name}</h1>
            </div>
            <ClientTimeline data={portal} />
            <ClientUpdates updates={updates} />
            <ClientReports reports={reports} />
          </>
        )}
      </main>
    </div>
  );
}
